import _ from 'lodash'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { useMemo } from 'react'
import { AppointmentModel, SchedulerDateTime } from '@devexpress/dx-react-scheduler'

import { Consultation } from '@/core/models/consultation.model'
import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { Unavailability } from '@/core/models/meeting-link.model'
import { OrderStatus } from '@/core/models/order.model'
import { Schedule, ScheduleRecurrenceType, Slot } from '@/core/models/schedule.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { SchedulerDataType } from '../enums/scheduler-data-type.enum'
import { getSchedulerDataFromConsultation, getSchedulerDataFromOrder } from '../utils/scheduler-data.util'
import { remove } from '@/utils/lodash-alt'

dayjs.extend(weekOfYear)

type SchedulerDataResource = {
  busySlots?: Slot[]
  consultations?: Consultation[]
  orders?: MeetingLinkOrder[]
  orderStatusFilter?: OrderStatus[]
  meetingLinkFilter?: string[]
  schedules?: Schedule[]
  unavailabilities?: Unavailability[]
}

export function useSchedulerData(
  { busySlots, consultations, orders, orderStatusFilter, meetingLinkFilter, schedules, unavailabilities }: SchedulerDataResource,
  currentDate: SchedulerDateTime,
  options: { scheduleColors: Record<string, string>; appointmentVisibilities: Record<string, boolean> }
) {
  const { scheduleColors, appointmentVisibilities } = options

  const authUser = useAppSelector(selectAuthUser)

  const consultationSchedulerData = useMemo(() => {
    if (!authUser || !appointmentVisibilities[authUser._id]) return []
    return _.map(consultations, (consultation) => getSchedulerDataFromConsultation(consultation))
  }, [appointmentVisibilities, consultations])

  const scheduleSchedulerData = useMemo(() => {
    if (!schedules) return []

    let data: AppointmentModel[] = []

    for (const schedule of schedules) {
      if (!appointmentVisibilities[schedule.createdBy._id]) continue

      const commonAppointmentData = {
        consultationReason: schedule.consultationReason,
        createdBy: schedule.createdBy,
        title: schedule.consultationReason,
        duration: schedule.duration,
        recurrenceRule: schedule.recurrenceRule,
        scheduleId: schedule._id,
        color: scheduleColors[schedule.createdBy._id],
        disableDelete: authUser?._id !== schedule.createdBy._id && authUser?.establishment?._id !== schedule.createdBy.establishment?._id,
        type: SchedulerDataType.SCHEDULE,
      }

      data.push({
        ...commonAppointmentData,
        startDate: dayjs(schedule.startDate).startOf('day').add(schedule.startTime, 'minutes').toDate(),
        endDate: dayjs(schedule.startDate).startOf('day').add(schedule.endTime, 'minutes').toDate(),
      })
      const { recurrenceRule } = schedule

      if (!recurrenceRule) continue

      const { daysOfWeek, datesOfMonth, monthsOfYear, separationCount } = recurrenceRule

      if (schedule.endDate) {
        switch (recurrenceRule.recurrenceType) {
          case ScheduleRecurrenceType.DAILY:
            const scheduleInstanceCount = Math.floor(dayjs(schedule.endDate).diff(dayjs(schedule.startDate), 'day') / separationCount)
            data.push(
              ..._.range(scheduleInstanceCount).map((index) => ({
                ...commonAppointmentData,
                startDate: dayjs(schedule.startDate)
                  .startOf('day')
                  .add((index + 1) * separationCount, 'day')
                  .add(schedule.startTime, 'minutes')
                  .toDate(),
                endDate: dayjs(schedule.startDate)
                  .startOf('day')
                  .add((index + 1) * separationCount, 'day')
                  .add(schedule.endTime, 'minutes')
                  .toDate(),
              }))
            )
            break
          case ScheduleRecurrenceType.WEEKLY:
            let weekOfYear = dayjs(schedule.startDate).isoWeek()
            while (weekOfYear <= dayjs(schedule.endDate).week()) {
              for (const dayOfWeek of daysOfWeek) {
                const scheduleInstanceDay = dayjs(schedule.startDate).isoWeek(weekOfYear).isoWeekday(dayOfWeek)
                if (dayjs(schedule.startDate).isSameOrAfter(scheduleInstanceDay) || scheduleInstanceDay.isAfter(schedule.endDate)) continue
                data.push({
                  ...commonAppointmentData,
                  startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                  endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
                })
              }
              weekOfYear += separationCount
            }
            break
          case ScheduleRecurrenceType.MONTHLY:
            let monthOfYear = dayjs(schedule.startDate).month()
            while (monthOfYear <= dayjs(schedule.endDate).month()) {
              for (const dateOfMonth of datesOfMonth) {
                const scheduleInstanceDay = dayjs(schedule.startDate).month(monthOfYear).date(dateOfMonth)
                if (scheduleInstanceDay.isBefore(schedule.startDate) || scheduleInstanceDay.isAfter(schedule.endDate)) continue
                data.push({
                  ...commonAppointmentData,
                  startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                  endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
                })
              }
              monthOfYear += separationCount
            }
            break
          case ScheduleRecurrenceType.YEARLY:
            let year = dayjs(schedule.startDate).year()
            while (year < dayjs(schedule.endDate).year()) {
              year += separationCount
              const scheduleInstanceDay = dayjs(schedule.startDate).year(year)
              if (scheduleInstanceDay.isBefore(schedule.startDate) || scheduleInstanceDay.isAfter(schedule.endDate)) continue
              data.push({
                ...commonAppointmentData,
                startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
              })
            }
            break
          default:
            break
        }
      } else {
        switch (recurrenceRule.recurrenceType) {
          case ScheduleRecurrenceType.DAILY:
            const startDateWeekday = dayjs(schedule.startDate).isoWeekday()
            const weekDiff = dayjs(currentDate).diff(schedule.startDate, 'week')

            if (weekDiff < 0) break

            const scheduleInstanceWeekday =
              weekDiff === 0 ? startDateWeekday : Math.ceil((1 + weekDiff * 7 - startDateWeekday) / separationCount) * separationCount - weekDiff * 7
            for (const dayOfWeek of _.range(1, 8)) {
              if (
                Math.abs(scheduleInstanceWeekday - dayOfWeek) % separationCount !== 0 ||
                (weekDiff === 0 && startDateWeekday === dayOfWeek) ||
                dayjs(schedule.startDate).isSameOrAfter(dayjs(currentDate).isoWeekday(dayOfWeek))
              ) {
                continue
              }
              data.push({
                ...commonAppointmentData,
                startDate: dayjs(currentDate).isoWeekday(dayOfWeek).startOf('day').add(schedule.startTime, 'minutes').toDate(),
                endDate: dayjs(currentDate).isoWeekday(dayOfWeek).startOf('day').add(schedule.endTime, 'minutes').toDate(),
              })
            }
            break
          case ScheduleRecurrenceType.WEEKLY:
            if ((dayjs(currentDate).isoWeek() - dayjs(schedule.startDate).isoWeek()) % separationCount !== 0) break
            for (const dayOfWeek of daysOfWeek) {
              const scheduleInstanceDay = dayjs(currentDate).isoWeekday(dayOfWeek)
              if (dayjs(schedule.startDate).isSameOrAfter(scheduleInstanceDay, 'day')) continue
              data.push({
                ...commonAppointmentData,
                startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
              })
            }
            break
          case ScheduleRecurrenceType.MONTHLY:
            if ((dayjs(currentDate).month() - dayjs(schedule.startDate).month()) % separationCount !== 0) break
            for (const dateOfMonth of datesOfMonth) {
              const scheduleInstanceDay = dayjs(currentDate).date(dateOfMonth)
              if (dayjs(schedule.startDate).isSameOrAfter(scheduleInstanceDay, 'day')) continue
              data.push({
                ...commonAppointmentData,
                startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
              })
            }
            break
          case ScheduleRecurrenceType.YEARLY:
            if ((dayjs(currentDate).year() - dayjs(schedule.startDate).year()) % separationCount !== 0) break
            for (const monthOfYear of monthsOfYear) {
              const scheduleInstanceDay = dayjs(currentDate).month(monthOfYear).date(dayjs(schedule.startDate).date())
              if (dayjs(schedule.startDate).isSameOrAfter(scheduleInstanceDay, 'day')) continue
              data.push({
                ...commonAppointmentData,
                startDate: scheduleInstanceDay.startOf('day').add(schedule.startTime, 'minutes').toDate(),
                endDate: scheduleInstanceDay.startOf('day').add(schedule.endTime, 'minutes').toDate(),
              })
            }
            break
        }
      }

      for (const scheduleException of schedule.exceptions) {
        if (scheduleException.isCancelled) {
          remove(data, (appointmentData) => {
            const { recurrenceRule } = scheduleException
            const scheduleExceptionStartDate = dayjs(scheduleException.startDate).startOf('day')
            if (!recurrenceRule) {
              return (
                dayjs(appointmentData.startDate).isSame(scheduleExceptionStartDate.add(scheduleException.startTime, 'minutes')) &&
                !!appointmentData.endDate &&
                dayjs(appointmentData.endDate).isSame(scheduleExceptionStartDate.add(scheduleException.endTime, 'minutes'))
              )
            }
            let matched =
              appointmentData.scheduleId === scheduleException.scheduleId && dayjs(appointmentData.startDate).isSameOrAfter(scheduleExceptionStartDate, 'day')

            if (scheduleException.endDate) {
              matched = matched && dayjs(scheduleException.endDate).isSameOrAfter(dayjs(appointmentData.startDate), 'day')
            }

            return matched
          })
        }
      }
    }

    if (!busySlots) return data

    for (const appointmentData of data) {
      for (const busySlot of busySlots) {
        const slotDuration = dayjs(busySlot.end).diff(busySlot.start, 'minutes')
        if (
          busySlot.owner !== appointmentData.createdBy._id ||
          slotDuration !== appointmentData.duration ||
          dayjs(busySlot.start).isBefore(appointmentData.startDate) ||
          dayjs(busySlot.end).isAfter(appointmentData.endDate)
        ) {
          continue
        }
        appointmentData['busySlots'] = appointmentData['busySlots'] ?? []
        appointmentData['busySlots'].push(busySlot)
      }
    }

    return data
  }, [appointmentVisibilities, authUser, busySlots, currentDate, schedules, scheduleColors])

  const orderSchedulerData = useMemo(() => {
    if (!orders) return []

    let filtered = orders
    if (orderStatusFilter && orderStatusFilter.length > 0) {
      filtered = filtered.filter((order) => orderStatusFilter.includes(order.status))
    }
    if (meetingLinkFilter && meetingLinkFilter.length > 0) {
      filtered = filtered.filter((order) => {
        const meetingLinkId = typeof order.receiverId === 'string' ? order.receiverId : ''
        return meetingLinkFilter.includes(meetingLinkId)
      })
    }

    return filtered.map(getSchedulerDataFromOrder)
  }, [orders, orderStatusFilter, meetingLinkFilter])

  const unavailabilitySchedulerData = useMemo(() => {
    if (!unavailabilities) return []

    return unavailabilities.map((u, index) => {
      const start = dayjs(u.startDate).startOf('day')
      const end = dayjs(u.endDate).startOf('day')

      const startDate = u.startTime != null
        ? start.add(u.startTime, 'minutes').toDate()
        : start.toDate()
      const endDate = u.endTime != null
        ? end.add(u.endTime, 'minutes').toDate()
        : end.endOf('day').toDate()

      return {
        id: `unavailability-${u._id}-${index}`,
        startDate,
        endDate,
        title: u.reason || 'Unavailable',
        color: '#e0e0e0',
        type: SchedulerDataType.UNAVAILABILITY,
      } as AppointmentModel
    })
  }, [unavailabilities])

  return useMemo(
    () => [...consultationSchedulerData, ...scheduleSchedulerData, ...orderSchedulerData, ...unavailabilitySchedulerData],
    [consultationSchedulerData, scheduleSchedulerData, orderSchedulerData, unavailabilitySchedulerData]
  )
}
